import React, { useState } from "react";
import axios from "axios";
import {
  Sparkles, Loader2, Building2, Briefcase, FileText, AlertTriangle,
  Search, Receipt, Users, ShieldCheck, ShieldAlert, CheckCircle2, XCircle,
  Eye, Clock, Network as NetworkIcon, History, Hash
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────────── Risk-band colour map ─────────────
const BAND_COLOR = {
  Critical: { bg: "rgba(220,38,38,0.16)", border: "#dc2626", text: "#fca5a5", glow: "0 0 24px rgba(220,38,38,0.35)" },
  High:     { bg: "rgba(239,68,68,0.12)", border: "#ef4444", text: "#ef4444", glow: "0 0 18px rgba(239,68,68,0.30)" },
  Medium:   { bg: "rgba(245,158,11,0.10)", border: "#f59e0b", text: "#f59e0b", glow: "0 0 14px rgba(245,158,11,0.25)" },
  Low:      { bg: "rgba(34,197,94,0.10)",  border: "#22c55e", text: "#22c55e", glow: "0 0 12px rgba(34,197,94,0.20)" },
  Unknown:  { bg: "rgba(148,163,184,0.08)", border: "#475569", text: "#94a3b8", glow: "none" },
};

const CIN_RE = /^[LUFP][0-9A-Z]{20}$/;

// ───────────── Small UI helpers ─────────────
function Pill({ icon: Icon, label, value, mono, color }) {
  return (
    <div style={{ background: "#080c12", borderRadius: 6, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
      {Icon && <Icon style={{ width: 14, height: 14, color: "#64748b" }} />}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
        <div style={{ color: color || "#f1f5f9", fontSize: 13, fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, badge, children, testid }) {
  return (
    <div data-testid={testid} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 18, marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          {Icon && <Icon style={{ width: 16, height: 16, color: "#60a5fa" }} />}
          <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, letterSpacing: ".2px", margin: 0 }}>{title}</h3>
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function Bullet({ children, color = "#94a3b8" }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px dashed rgba(148,163,184,0.10)" }}>
      <div style={{ width: 6, height: 6, borderRadius: 999, background: color, marginTop: 8, flexShrink: 0 }} />
      <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

// ───────────── Main page ─────────────
export default function KybOneClickPage() {
  const [cin, setCin] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [articleText, setArticleText] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("");
  const [dossier, setDossier] = useState(null);
  const [history, setHistory] = useState([]);

  React.useEffect(() => {
    void fetchHistory();
  }, []);

  async function fetchHistory() {
    try {
      const t = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/kyb/investigations?limit=10`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setHistory(data.investigations || []);
    } catch (e) { /* silent */ }
  }

  async function runOneClick() {
    if (!CIN_RE.test((cin || "").trim().toUpperCase())) {
      toast.error("Enter a valid 21-char CIN (e.g. L36911MH1986PLC040689)");
      return;
    }
    setLoading(true);
    setDossier(null);
    setPhase("Fetching MCA21 + GST + Adverse Media in parallel…");

    // Visual phase ticker — gives user something to watch during the 45s call
    const phases = [
      "Fetching MCA21 + GST + Adverse Media in parallel…",
      "Parsing director / DIN data from MCA21…",
      "Running Claude Sonnet 4.5 KYB synthesis (RBI/PMLA citations)…",
      "Persisting audit-ready dossier…",
    ];
    let idx = 0;
    const ticker = setInterval(() => {
      idx = Math.min(idx + 1, phases.length - 1);
      setPhase(phases[idx]);
    }, 12000);

    try {
      const t = localStorage.getItem("token");
      const body = { cin: cin.trim().toUpperCase() };
      if (companyName.trim()) body.company_name = companyName.trim();
      if (articleText.trim().length >= 30) body.article_text = articleText.trim();
      const { data } = await axios.post(`${API}/kyb/one-click`, body, {
        headers: { Authorization: `Bearer ${t}` },
        timeout: 90_000,
      });
      setDossier(data);
      toast.success(`Dossier ready — ${(data.results?.kyb_analysis || {}).risk_level || "—"} risk`);
      await fetchHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`KYB pipeline failed: ${detail}`);
    } finally {
      clearInterval(ticker);
      setLoading(false);
      setPhase("");
    }
  }

  async function loadHistorical(id) {
    setLoading(true);
    try {
      const t = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/kyb/investigations/${id}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setDossier(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Failed to load investigation");
    } finally {
      setLoading(false);
    }
  }

  const ka = dossier?.results?.kyb_analysis || null;
  const mca = dossier?.results?.mca21 || null;
  const gst = dossier?.results?.gst || null;
  const directors = dossier?.results?.directors || [];
  const adverseMedia = dossier?.results?.adverse_media || null;
  const band = BAND_COLOR[ka?.risk_level] || BAND_COLOR.Unknown;

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "24px" }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center" style={{ gap: 12, marginBottom: 6 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Sparkles style={{ width: 18, height: 18, color: "#60a5fa" }} />
        </div>
        <div>
          <h1 style={{ color: "#f1f5f9", fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: "-.3px" }}>
            KYB One-Click <span style={{ color: "#60a5fa" }}>Investigation</span>
          </h1>
          <p style={{ color: "#94a3b8", margin: "2px 0 0 0", fontSize: 13 }}>
            Enter a CIN — backend fan-outs MCA21 + GST + Adverse Media in parallel, then Claude Sonnet 4.5 synthesises an audit-ready KYB dossier with PMLA / RBI / Companies Act citations.
          </p>
        </div>
      </div>

      {/* ── Input panel ────────────────────────────────────── */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20, marginTop: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 12 }}>
          <div>
            <label style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>CIN (required)</label>
            <input
              data-testid="kyb-cin-input"
              value={cin}
              onChange={e => setCin(e.target.value.toUpperCase())}
              placeholder="L36911MH1986PLC040689"
              style={{ marginTop: 6, width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "12px 14px", color: "#f1f5f9", fontSize: 14, fontFamily: "ui-monospace, monospace", outline: "none" }}
              disabled={loading}
            />
          </div>
          <div>
            <label style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>Company name (optional — MCA21 will resolve)</label>
            <input
              data-testid="kyb-company-input"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="e.g. Gitanjali Gems Limited"
              style={{ marginTop: 6, width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "12px 14px", color: "#f1f5f9", fontSize: 14, outline: "none" }}
              disabled={loading}
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>Adverse-media article (optional — paste news/ED-press text, min 30 chars)</label>
          <textarea
            data-testid="kyb-article-input"
            value={articleText}
            onChange={e => setArticleText(e.target.value)}
            rows={3}
            placeholder="Optional: paste a related news article to enrich the dossier with adverse-media signals…"
            style={{ marginTop: 6, width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "12px 14px", color: "#f1f5f9", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit" }}
            disabled={loading}
          />
        </div>

        <div className="flex items-center" style={{ justifyContent: "space-between", marginTop: 14 }}>
          <div style={{ color: "#475569", fontSize: 11 }}>
            ⏱ ~45-50s end-to-end · Powered by Claude Sonnet 4.5 + MCA21 + GST + Adverse-Media
          </div>
          <button
            data-testid="kyb-run-button"
            onClick={runOneClick}
            disabled={loading}
            style={{
              background: loading ? "#1e2530" : "linear-gradient(135deg, #2563eb, #3b82f6)",
              color: "white", border: "none", borderRadius: 8, padding: "12px 24px",
              fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: 8,
              boxShadow: loading ? "none" : "0 0 18px rgba(59,130,246,0.40)",
              transition: "all .25s ease",
            }}
          >
            {loading ? <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> : <Sparkles style={{ width: 16, height: 16 }} />}
            {loading ? "Investigating…" : "Run One-Click Investigation"}
          </button>
        </div>

        {loading && phase && (
          <div data-testid="kyb-phase-indicator" style={{ marginTop: 16, padding: "12px 14px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.30)", borderRadius: 8, color: "#93c5fd", fontSize: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            {phase}
          </div>
        )}
      </div>

      {/* ── Dossier output ─────────────────────────────────── */}
      {dossier && (
        <div data-testid="kyb-dossier" style={{ marginTop: 24 }}>
          {/* Verdict banner */}
          <div style={{
            background: band.bg, border: `1px solid ${band.border}`, borderRadius: 12,
            padding: 22, marginBottom: 18, boxShadow: band.glow,
          }}>
            <div className="flex items-center" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div className="flex items-center" style={{ gap: 14 }}>
                <Building2 style={{ width: 26, height: 26, color: band.text }} />
                <div>
                  <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>{dossier.cin}</div>
                  <div style={{ color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: "2px 0" }}>{dossier.company_name}</div>
                </div>
              </div>
              <div className="flex items-center" style={{ gap: 18 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>Risk Score</div>
                  <div style={{ color: band.text, fontSize: 38, fontWeight: 800, lineHeight: 1 }} data-testid="kyb-risk-score">{ka?.overall_risk_score ?? "—"}<span style={{ color: "#475569", fontSize: 14 }}> /100</span></div>
                </div>
                <div style={{ background: "#080c12", border: `1px solid ${band.border}`, borderRadius: 8, padding: "8px 14px" }} data-testid="kyb-risk-band">
                  <div style={{ color: band.text, fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>{ka?.risk_level?.toUpperCase() ?? "—"}</div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16, color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>
              {ka?.company_overview || "—"}
            </div>
            <div className="flex items-center" style={{ gap: 14, marginTop: 14, flexWrap: "wrap" }}>
              {(ka?.recommended_actions || []).map((act, i) => (
                <div key={i} style={{ background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.35)", borderRadius: 999, padding: "6px 14px", color: "#93c5fd", fontSize: 12, fontWeight: 600 }}>
                  <CheckCircle2 style={{ width: 12, height: 12, display: "inline", marginRight: 6 }} />
                  {act}
                </div>
              ))}
            </div>
            <div className="flex items-center" style={{ gap: 16, marginTop: 14, color: "#64748b", fontSize: 11 }}>
              <span><Clock style={{ width: 12, height: 12, display: "inline", marginRight: 4 }} />{dossier.duration_ms}ms</span>
              <span><Hash style={{ width: 12, height: 12, display: "inline", marginRight: 4 }} />{dossier.id?.slice(0, 12)}</span>
              <span>AI confidence: <strong style={{ color: "#cbd5e1" }}>{ka?.confidence_level ?? "—"}%</strong></span>
              <span>Model: <strong style={{ color: "#cbd5e1" }}>{ka?.ai_mode || "—"}</strong></span>
            </div>
          </div>

          {/* Two-column grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <SectionCard title="Key Risk Factors" icon={ShieldAlert} testid="kyb-risk-factors">
                {(ka?.key_risk_factors || []).length === 0 && (
                  <div style={{ color: "#64748b", fontSize: 13 }}>No material risk factors identified.</div>
                )}
                {(ka?.key_risk_factors || []).map((f, i) => (
                  <Bullet key={i} color="#ef4444">{f}</Bullet>
                ))}
              </SectionCard>

              <SectionCard title="Key Strengths" icon={ShieldCheck} testid="kyb-strengths">
                {(ka?.key_strengths || []).length === 0 && (
                  <div style={{ color: "#64748b", fontSize: 13 }}>—</div>
                )}
                {(ka?.key_strengths || []).map((s, i) => (
                  <Bullet key={i} color="#22c55e">{s}</Bullet>
                ))}
              </SectionCard>

              <SectionCard title="Regulatory Concerns" icon={FileText} testid="kyb-regulatory">
                <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>
                  {ka?.regulatory_concerns || "—"}
                </div>
              </SectionCard>
            </div>

            <div>
              <SectionCard
                title="MCA21 Filing Snapshot"
                icon={Briefcase}
                badge={<span style={{ color: "#94a3b8", fontSize: 11 }}>{mca?.ai_mode}</span>}
                testid="kyb-mca21"
              >
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <Pill icon={Building2} label="Status" value={mca?.company_status || mca?.status} color={(mca?.company_status || "").toLowerCase().includes("strike") ? "#ef4444" : "#22c55e"} />
                  <Pill icon={FileText} label="ROC" value={mca?.roc} />
                  <Pill icon={Receipt} label="Paid-up" value={mca?.paid_up_capital ? `₹${(mca.paid_up_capital / 1e7).toFixed(2)} Cr` : "—"} mono />
                  <Pill icon={Receipt} label="Authorised" value={mca?.authorised_capital ? `₹${(mca.authorised_capital / 1e7).toFixed(2)} Cr` : "—"} mono />
                </div>
                <div style={{ marginTop: 10 }}>
                  <Pill icon={FileText} label="Last AGM" value={mca?.last_agm_date} mono />
                </div>
                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
                  <strong style={{ color: "#cbd5e1" }}>Directors summary:</strong> {ka?.directors_summary || "—"}
                </div>
              </SectionCard>

              <SectionCard
                title="GST Compliance"
                icon={Receipt}
                badge={<span style={{ color: "#94a3b8", fontSize: 11 }}>{gst?.ai_mode}</span>}
                testid="kyb-gst"
              >
                <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>
                  {ka?.gst_compliance || "—"}
                </div>
                {gst?.gstin && (
                  <div style={{ marginTop: 10 }}>
                    <Pill icon={Hash} label="GSTIN" value={gst.gstin} mono />
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title={`Directors on Board (${directors.length})`}
                icon={Users}
                testid="kyb-directors"
              >
                {directors.length === 0 && (
                  <div style={{ color: "#64748b", fontSize: 13 }}>—</div>
                )}
                {directors.slice(0, 8).map((d, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: "1px dashed rgba(148,163,184,0.10)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div style={{ overflow: "hidden" }}>
                      <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 600 }}>{d.full_name || d.name || "—"}</div>
                      <div style={{ color: "#64748b", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>DIN {d.din || "—"} · {d.designation || d.role || "Director"}</div>
                    </div>
                    {d.is_disqualified && (
                      <span style={{ background: "rgba(239,68,68,0.12)", border: "1px solid #ef4444", borderRadius: 999, color: "#fca5a5", fontSize: 10, padding: "2px 8px", fontWeight: 700 }}>DISQUALIFIED</span>
                    )}
                    {d.shell_signal && (
                      <span style={{ background: "rgba(245,158,11,0.10)", border: "1px solid #f59e0b", borderRadius: 999, color: "#fbbf24", fontSize: 10, padding: "2px 8px", fontWeight: 700 }}>SHELL?</span>
                    )}
                  </div>
                ))}
              </SectionCard>

              {adverseMedia && adverseMedia.ai_mode !== "skipped" && (
                <SectionCard title="Adverse Media" icon={AlertTriangle} testid="kyb-adverse-media">
                  <div style={{ color: "#cbd5e1", fontSize: 13, marginBottom: 8 }}>
                    Severity: <strong style={{ color: band.text }}>{adverseMedia.overall_severity || "—"}</strong>
                    {" · "}Sentiment: <strong>{adverseMedia.sentiment || "—"}</strong>
                  </div>
                  {(adverseMedia.key_facts || []).slice(0, 5).map((f, i) => (
                    <Bullet key={i} color="#f59e0b">{f}</Bullet>
                  ))}
                </SectionCard>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── History panel ──────────────────────────────────── */}
      {history.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div className="flex items-center" style={{ gap: 8, marginBottom: 10 }}>
            <History style={{ width: 14, height: 14, color: "#64748b" }} />
            <h3 style={{ color: "#cbd5e1", fontSize: 13, fontWeight: 700, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>Recent Investigations ({history.length})</h3>
          </div>
          <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
            {history.map((h, i) => (
              <div
                key={h.id}
                data-testid="kyb-history-row"
                onClick={() => loadHistorical(h.id)}
                style={{
                  padding: "12px 16px",
                  borderBottom: i < history.length - 1 ? "1px solid #1e2530" : "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  cursor: "pointer", transition: "background .15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#11161f"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div>
                  <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 600 }}>{h.company_name}</div>
                  <div style={{ color: "#64748b", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{h.cin} · {h.duration_ms}ms · {new Date(h.started_at).toLocaleString()}</div>
                </div>
                <Eye style={{ width: 14, height: 14, color: "#475569" }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
